import { useState, type ReactNode, type SVGProps } from "react"
import { Link, router } from "@inertiajs/react"
import { useShared } from "@/lib/inertia"
import { Logo } from "./Logo"
import { Flash } from "./Flash"
import { cn } from "@/lib/cn"

type NavItem = {
  label: string
  href: string
  icon: (props: SVGProps<SVGSVGElement>) => ReactNode
  match?: (p: string) => boolean
}

function GaugeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M3 13a9 9 0 1 1 18 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="m12 13 5-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="13" r="1.3" fill="currentColor" />
    </svg>
  )
}
function UsersIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="9" cy="9" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 11a3 3 0 1 0 0-6m4.5 14a5.5 5.5 0 0 0-5.5-5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function BuildingIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M4 21V5l8-2 8 2v16M9 21V11M15 21V11M9 7h.01M15 7h.01M9 14h.01M15 14h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function ListIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M4 6.5h16M4 12h16M4 17.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function PlayIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M7 5v14l11-7-11-7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}
function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="m4 12 5 5 11-12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function CoinsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <ellipse cx="9" cy="8" rx="6" ry="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 8v4c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V8" stroke="currentColor" strokeWidth="1.5" />
      <ellipse cx="15" cy="14" rx="6" ry="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 14v4c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5v-4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}
function AuditIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function KeyIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M14 14a5 5 0 1 0-3.5-8.6L4 12v3h3l1-1v-2h2v-2h2l1.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function WebhookIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="18" cy="18" r="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="6" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="m8 16 3-6M16 16l-2-4M9 18h6" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}
function StripeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M14 7c-2 0-3 .8-3 2 0 2.5 6 1.5 6 5 0 2-1.7 3-4 3s-4-1-5-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function MailIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="m3 7 9 7 9-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
function ShieldIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M12 3 4 6v6c0 4.5 3.5 8 8 9 4.5-1 8-4.5 8-9V6l-8-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  )
}
function MonitorIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <rect x="3" y="4" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function MenuIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-5", className)} aria-hidden="true">
      <path d="M3 5h10M3 8h10M3 11h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-5", className)} aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
function MailboxIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <rect x="3" y="8" width="18" height="11" rx="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 8V4M9 13h.01M15 13h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

const observabilityNav: NavItem[] = [
  { label: "Overview", href: "/admin", icon: GaugeIcon, match: (p) => p === "/admin" },
  { label: "Audit log", href: "/admin/audit_events", icon: AuditIcon, match: (p) => p.startsWith("/admin/audit_events") },
  { label: "Stripe events", href: "/admin/stripe_events", icon: StripeIcon, match: (p) => p.startsWith("/admin/stripe_events") },
]
const accountsNav: NavItem[] = [
  { label: "Users", href: "/admin/users", icon: UsersIcon, match: (p) => p.startsWith("/admin/users") },
  { label: "Workspaces", href: "/admin/workspaces", icon: BuildingIcon, match: (p) => p.startsWith("/admin/workspaces") },
  { label: "Invitations", href: "/admin/invitations", icon: MailIcon, match: (p) => p.startsWith("/admin/invitations") },
  { label: "Sessions", href: "/admin/sessions", icon: MonitorIcon, match: (p) => p.startsWith("/admin/sessions") },
]
const productNav: NavItem[] = [
  { label: "Lists", href: "/admin/lists", icon: ListIcon, match: (p) => p.startsWith("/admin/lists") },
  { label: "List runs", href: "/admin/list_runs", icon: PlayIcon, match: (p) => p.startsWith("/admin/list_runs") },
  { label: "Verifications", href: "/admin/verifications", icon: CheckIcon, match: (p) => p.startsWith("/admin/verifications") },
  { label: "API keys", href: "/admin/api_keys", icon: KeyIcon, match: (p) => p.startsWith("/admin/api_keys") },
  { label: "Webhooks", href: "/admin/webhooks", icon: WebhookIcon, match: (p) => p.startsWith("/admin/webhooks") },
]
const opsNav: NavItem[] = [
  { label: "Credits ledger", href: "/admin/credit_ledger_entries", icon: CoinsIcon, match: (p) => p.startsWith("/admin/credit_ledger_entries") },
  { label: "Contact messages", href: "/admin/contact_messages", icon: MailboxIcon, match: (p) => p.startsWith("/admin/contact_messages") },
  { label: "Disposable domains", href: "/admin/disposable_domains", icon: ShieldIcon, match: (p) => p.startsWith("/admin/disposable_domains") },
]

function NavLink({ item, pathname, onNavigate }: { item: NavItem; pathname: string; onNavigate?: () => void }) {
  const active = item.match ? item.match(pathname) : pathname === item.href
  const Icon = item.icon
  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      data-testid={`admin-nav-${item.label.toLowerCase().replaceAll(" ", "-")}`}
      className={cn(
        "group flex items-center gap-x-2.5 rounded-lg px-2.5 py-2 text-sm tracking-tight transition",
        active
          ? "bg-ink-950 text-white"
          : "text-ink-300 hover:bg-ink-800/60 hover:text-white",
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn(
          "size-4 shrink-0 transition",
          active ? "text-white" : "text-ink-500 group-hover:text-ink-200",
        )}
      />
      <span className="truncate">{item.label}</span>
    </Link>
  )
}

function NavGroup({ label, items, pathname, onNavigate }: { label: string; items: NavItem[]; pathname: string; onNavigate?: () => void }) {
  return (
    <nav className="flex flex-col gap-y-0.5" aria-label={label}>
      <p className="px-2.5 pb-1 text-[11px] font-medium tracking-wide text-ink-500 uppercase">{label}</p>
      {items.map((item) => (
        <NavLink key={item.href} item={item} pathname={pathname} onNavigate={onNavigate} />
      ))}
    </nav>
  )
}

function ImpersonationBanner() {
  const { auth } = useShared()
  if (!auth.impersonating || !auth.true_user) return null
  return (
    <div className="sticky top-0 z-40 flex items-center justify-between gap-x-3 border-b border-amber-300 bg-amber-100 px-4 py-2 text-amber-900">
      <p className="text-sm tracking-tight" data-testid="impersonation-banner">
        <span className="font-semibold">Impersonating</span> {auth.user?.email}.{" "}
        Original: {auth.true_user.email}.
      </p>
      <button
        type="button"
        onClick={() => router.delete("/admin/impersonation")}
        className="rounded-md bg-amber-900 px-3 py-1 text-xs font-medium text-amber-50 hover:bg-amber-800"
        data-testid="stop-impersonating"
      >
        Stop impersonating
      </button>
    </div>
  )
}

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { pathname, auth } = useShared()
  const [mobileOpen, setMobileOpen] = useState(false)
  const firstName = auth.true_user?.name?.split(" ")[0] ?? auth.user?.name?.split(" ")[0]

  return (
    <div className="isolate min-h-dvh bg-ink-50/40 text-ink-950">
      <ImpersonationBanner />
      <div className="flex min-h-dvh flex-col lg:flex-row">
        <Flash />

        {/* Mobile topbar */}
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-ink-800 bg-ink-950 px-4 py-3 lg:hidden">
          <Link href="/admin" aria-label="CleanMyList admin" className="inline-flex items-center">
            <Logo className="text-xl text-white" />
            <span className="ml-2 rounded-md bg-brand-500/20 px-2 py-0.5 text-[10px] font-semibold text-brand-300 ring-1 ring-brand-500/30">
              ADMIN
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className="inline-flex size-9 items-center justify-center rounded-lg text-ink-200 hover:bg-ink-800"
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </header>

        {/* Sidebar */}
        <aside
          className={cn(
            "z-20 w-full shrink-0 border-ink-800 bg-ink-950 text-white lg:flex lg:w-72 lg:flex-col lg:border-r",
            mobileOpen ? "block" : "hidden lg:block",
          )}
        >
          <div className="flex h-full flex-col gap-y-6 px-4 py-6 lg:px-5 lg:py-7">
            <div className="hidden px-1 lg:flex lg:items-center">
              <Link href="/admin" aria-label="CleanMyList admin" className="inline-flex items-center">
                <Logo className="text-white" />
              </Link>
              <span className="ml-2 rounded-md bg-brand-500/20 px-2 py-0.5 text-[10px] font-semibold text-brand-300 ring-1 ring-brand-500/30">
                ADMIN
              </span>
            </div>

            <Link
              href="/app"
              className="flex items-center gap-x-2 rounded-lg bg-ink-800/60 px-2.5 py-2 text-sm text-ink-200 hover:bg-ink-800 hover:text-white"
              data-testid="admin-back-to-app"
            >
              <svg viewBox="0 0 16 16" className="size-4" fill="none" aria-hidden="true">
                <path d="M10 4 6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to app
            </Link>

            <div className="flex flex-col gap-y-5">
              <NavGroup label="Observability" items={observabilityNav} pathname={pathname} onNavigate={() => setMobileOpen(false)} />
              <NavGroup label="Accounts" items={accountsNav} pathname={pathname} onNavigate={() => setMobileOpen(false)} />
              <NavGroup label="Product" items={productNav} pathname={pathname} onNavigate={() => setMobileOpen(false)} />
              <NavGroup label="Operations" items={opsNav} pathname={pathname} onNavigate={() => setMobileOpen(false)} />
            </div>

            <div className="mt-auto flex items-center gap-x-2.5 rounded-xl bg-ink-800/60 px-3 py-2 ring-1 ring-ink-700">
              <span
                aria-hidden="true"
                className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-500 text-xs font-semibold text-ink-950"
              >
                {firstName?.charAt(0)?.toUpperCase() ?? "?"}
              </span>
              <div className="min-w-0 grow">
                <p className="truncate text-sm font-medium tracking-tight text-white">{auth.true_user?.name ?? auth.user?.name}</p>
                <p className="truncate text-xs text-ink-400">{auth.true_user?.email ?? auth.user?.email}</p>
              </div>
              <Link
                href="/sign-out"
                method="delete"
                as="button"
                aria-label="Sign out"
                className="grid size-7 shrink-0 place-items-center rounded-lg text-ink-300 hover:bg-ink-800 hover:text-white"
              >
                <svg viewBox="0 0 24 24" className="size-4" fill="none" aria-hidden="true">
                  <path
                    d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </aside>

        <main className="flex-1 overflow-x-hidden">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-10 lg:py-10">{children}</div>
        </main>
      </div>
    </div>
  )
}
